import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import SES from '@/ses'
import { updateHelpHero } from '@/utils/helpHero'
import errorReporter from '@/utils/errorReporter'

export function useInviteTeamMembers () {
  const store = useStore()
  const route = useRoute()

  const isInviting = ref(false)

  const project = store.getters['projectMemberships/activeProject']
  const currentUser = store.state.user.user

  // Returns the plan's members once seating is done, or null if it failed. The store's own refresh
  // after seating is not awaited, so the invitation email cannot read it.
  const seatInvitees = async (emailsToInvite, emailsNeedingSeats) => {
    const payments = store.state.payments

    const seated = (payments.subscriptionUsers || []).map((user) => user.email)
    const candidates = emailsToInvite.filter((email) => !seated.includes(email))

    if (candidates.length === 0) {
      return seated
    }

    const seatingFailed = (result) => {
      store.commit('snackbar/showMessage', {
        content: result?.message || 'The seat could not be added, so no invitations were sent.',
        timeout: 8000,
        color: 'red'
      }, { root: true })

      return null
    }

    const seatedWith = (emails) => [...seated, ...emails]

    // sendInvitationEmail is off on both paths because the project invitation below replaces the
    // generic seat one, which would otherwise reach the same person twice.
    // Enterprise seats are agreed up front, so going over the agreement still returns success and
    // is settled with the account manager — only a genuine failure blocks the invite.
    if (payments.isEnterpriseUser && payments.isSubscriptionAdmin) {
      const result = await store.dispatch('payments/addUsersToEnterprisePlan', {
        users: candidates.map((email) => ({ email })),
        sendInvitationEmail: false
      })

      return result?.success ? seatedWith(candidates) : seatingFailed(result)
    }

    // Stripe refuses anyone who already pays for themselves, and one such address fails the whole
    // batch, so only addresses without a subscription of their own are sent.
    const classifyCandidates = async () => {
      const res = await store.dispatch('payments/validateUsersSubscriptions', { users: candidates.map((email) => ({ email })) })
      const verdicts = res?.users || []

      // Fewer verdicts than candidates is a failed lookup, not "nobody needs a seat".
      if (verdicts.length < candidates.length) {
        return null
      }

      return verdicts
        .filter((user) => user.status !== 'active')
        .map((user) => user.email)
    }

    // An empty array means the caller checked and found nobody; only null means it did not check.
    const classified = emailsNeedingSeats ?? await classifyCandidates()

    if (!classified) {
      return seatingFailed({ message: 'Could not check who needs a seat, so no invitations were sent.' })
    }

    const withoutSubscription = candidates.filter((email) => classified.includes(email))

    if (withoutSubscription.length === 0) {
      return seated
    }

    // A non-payer can only get here by skipping the seat check; refuse rather than invite unseated.
    if (!payments.isSubscriptionAdmin) {
      return seatingFailed({ message: 'Only the person who pays for the plan can add seats, so no invitations were sent.' })
    }

    const result = await store.dispatch('payments/addUsersToSubscription', {
      users: withoutSubscription.map((email) => ({ email })),
      sendInvitationEmail: false
    })

    return result?.success ? seatedWith(withoutSubscription) : seatingFailed(result)
  }

  const inviteTeamMembers = async (emailsToInvite, workflowName = null, emailsNeedingSeats = null) => {
    if (!emailsToInvite || emailsToInvite.length === 0) {
      return
    }

    isInviting.value = true
    let allInvitationsSuccessful = true
    const successfulInvites = []
    const failedInvites = []

    // A failed seat purchase means the invitee would land read-only, so nothing is invited.
    const seatedEmails = await seatInvitees(emailsToInvite, emailsNeedingSeats)

    if (!seatedEmails) {
      isInviting.value = false

      return { allInvitationsSuccessful: false, successfulInvites: [], failedInvites: [...emailsToInvite] }
    }

    for (const emailToInvite of emailsToInvite) {
      try {
        const res = await store.dispatch('teamMembers/createTeamMember', {
          email: emailToInvite,
          projectId: route.params.projectId
        })

        if (res) {
          try {
            const emailService = new SES()
            await emailService.sendEmail(
              emailToInvite,
              project,
              currentUser.firstname + ' ' + currentUser.lastname,
              workflowName,
              {
                // The resolver stores an email instead of a userId when the invitee has no account yet.
                isRegistered: !!res.userId,
                isSeated: seatedEmails.includes(emailToInvite)
              }
            )
            successfulInvites.push(emailToInvite)
          } catch (error) {
            errorReporter.reportError({
              message: error.message,
              stack: error.stack || '',
              type: 'invitation-email-error',
              extra: { errorName: error.name, projectId: route.params.projectId }
            })
            failedInvites.push(emailToInvite)
            store.commit('snackbar/showMessage', {
              content: `${emailToInvite} was added to the project, but the invitation email could not be sent.`,
              timeout: 6000,
              color: 'red'
            }, { root: true })
            allInvitationsSuccessful = false
          }
        }
      } catch (error) {
        console.error(`Error creating team member ${emailToInvite}:`, error)
        failedInvites.push(emailToInvite)
        store.commit('snackbar/showMessage', {
          content: `Could not invite ${emailToInvite}`,
          timeout: 6000,
          color: 'red'
        }, { root: true })
        allInvitationsSuccessful = false
      }
    }

    if (successfulInvites.length > 0) {
      updateHelpHero({ hasInvitedTeammate: true })
      store.commit('snackbar/showMessage', {
        content: successfulInvites.length === 1
          ? `Invitation sent to ${successfulInvites[0]}`
          : `${successfulInvites.length} invitations sent`
      }, { root: true })
    }

    isInviting.value = false

    return { allInvitationsSuccessful, successfulInvites, failedInvites }
  }

  return {
    inviteTeamMembers,
    isInviting
  }
}
